// @ts-check
// Cliente API centralizado (03-frontend §9.1). Los componentes NO llaman fetch
// directamente; toda petición pasa por aquí. Sin logs de body/token.
import { config } from '../app/config.js';
import { ApiError, classifyStatus } from './api-errors.js';

const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * @typedef {{
 *   method?: string,
 *   body?: unknown,
 *   signal?: AbortSignal,
 *   timeoutMs?: number,
 *   credentials?: RequestCredentials,
 *   headers?: Record<string, string>,
 * }} RequestOptions
 */

/**
 * `valor` si es un texto con algo dentro; `undefined` en cualquier otro caso.
 * @param {unknown} valor
 * @returns {string | undefined}
 */
function textoNoVacio(valor) {
  return typeof valor === 'string' && valor.trim().length > 0 ? valor : undefined;
}

/**
 * Extrae `title` y `type` del cuerpo de una respuesta de error, si los trae.
 * Nunca lanza: un fallo al leer el cuerpo no puede sustituir al error real.
 * @param {Response} response
 * @returns {Promise<{ title?: string, code?: string }>}
 */
async function leerCuerpoDeError(response) {
  try {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) return {};
    const cuerpo = /** @type {Record<string, unknown>} */ ((await response.json()) ?? {});
    return { title: textoNoVacio(cuerpo.title), code: textoNoVacio(cuerpo.type) };
  } catch {
    return {};
  }
}

/**
 * Realiza una petición al backend con timeout, cancelación y errores normalizados.
 * Usa cookies de sesión solo cuando se solicita explícitamente (credentials).
 * @param {string} path - Ruta relativa a la base de la API (p.ej. "/users/me").
 * @param {RequestOptions} [options]
 * @returns {Promise<unknown>}
 */
export async function apiRequest(path, options = {}) {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Si el llamador pasa su propio signal, propagamos su cancelación también.
  options.signal?.addEventListener('abort', () => controller.abort());

  let response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers: {
        Accept: 'application/json',
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        // Cabeceras explícitas del llamador (p.ej. `X-CSRF-Token` en
        // mutaciones autenticadas, ver `services/auth-client.js#cerrarSesion`)
        // se combinan DESPUÉS de las por defecto: se añaden sin perder
        // `Accept`/`Content-Type` salvo que el propio llamador decida
        // sobrescribirlas explícitamente con el mismo nombre de cabecera.
        ...(options.headers ?? {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      credentials: options.credentials ?? 'same-origin',
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (/** @type {Error} */ (err).name === 'AbortError') {
      throw new ApiError('timeout');
    }
    throw new ApiError('network_error');
  }
  clearTimeout(timeoutId);

  const requestId = response.headers.get('x-request-id') ?? undefined;

  if (!response.ok) {
    // `ErrorResponse.title` (backend/src/app/schemas/errors.py) está definido
    // como "mensaje seguro para el usuario final" y lo construye
    // `_safe_title`, que nunca deja pasar texto de excepción sin sanear.
    // Hasta ahora se descartaba, y eso obligaba a la interfaz a adivinar el
    // significado de un status: un 409 podía ser "no admite autocorrección",
    // "no has iniciado intento" o "ya lo has completado", y el mensaje tenía
    // que ser tan genérico que a veces decía algo falso.
    //
    // Se lee de forma DEFENSIVA: un cuerpo vacío, no-JSON o sin `title` deja
    // `title` en `undefined` y quien lo consume cae a su mensaje de siempre.
    //
    // `type` (el código estable, `ErrorCode`) viaja en `code` desde el
    // 2026-09-13, con la misma cautela: distingue dos fallos con el mismo
    // status -- la cuenta pendiente de aprobar frente a cualquier otro 403,
    // que puede venir de un proxy y no traer cuerpo nuestro.
    const { title, code } = await leerCuerpoDeError(response);
    throw new ApiError(classifyStatus(response.status), {
      status: response.status,
      requestId,
      code,
      title,
    });
  }

  // Un 204 no trae cuerpo aunque FastAPI lo marque `application/json`: leerlo
  // con `response.json()` lanzaba, y una operación que había salido bien se
  // enseñaba como un error (el cambio de contraseña del perfil, 2026-09-12).
  const contentType = response.headers.get('content-type') ?? '';
  if (response.status === 204 || !contentType.includes('application/json')) {
    return null;
  }
  return response.json();
}
